// Thin API client for the coin economy + team rewards surfaces.
// Centralizes path strings and the small amount of request
// shaping needed (e.g. ?team_id=X for the player list).

import { api, ApiError } from '../../lib/api';
import type {
  CoinTransaction,
  TeamReward,
  RewardContributor,
  RewardContribution,
  ContributedResult,
} from './types';

export { ApiError };

export async function getCoinBalance(): Promise<number> {
  const res = await api<{ balance: number }>('/api/me/coins');
  return res.balance;
}

export async function listCoinTransactions(limit = 20): Promise<CoinTransaction[]> {
  const res = await api<{ transactions: CoinTransaction[] }>(
    `/api/me/coin-transactions?limit=${limit}`,
  );
  return res.transactions;
}

export async function getMyTeamRewards(teamId: string): Promise<TeamReward[]> {
  const res = await api<{ rewards: TeamReward[] }>(
    `/api/me/team-rewards?team_id=${encodeURIComponent(teamId)}`,
  );
  return res.rewards;
}

export async function getMyTeamReward(rewardId: string): Promise<TeamReward> {
  const res = await api<{ reward: TeamReward }>(
    `/api/me/team-rewards/${encodeURIComponent(rewardId)}`,
  );
  return res.reward;
}

export async function contribute(
  rewardId: string,
  amount: number,
): Promise<ContributedResult> {
  return api<ContributedResult>(
    `/api/me/team-rewards/${encodeURIComponent(rewardId)}/contribute`,
    { method: 'POST', body: { amount } },
  );
}

export async function getRewardContributors(rewardId: string): Promise<RewardContributor[]> {
  const res = await api<{ contributors: RewardContributor[] }>(
    `/api/me/team-rewards/${encodeURIComponent(rewardId)}/contributors`,
  );
  return res.contributors;
}

// M5.12: peer-to-peer coin transfer. Returns the new
// sender balance plus a confirmation object the modal
// uses to display success.
export interface TransferResult {
  ok: true;
  transfer_id: string;
  amount: number;
  to: { id: string; display_name: string };
  new_balance: number;
}

export async function transferCoins(
  toEmailOrUserId: string,
  amount: number,
  note?: string,
): Promise<TransferResult> {
  return api<TransferResult>('/api/me/coins/transfer', {
    method: 'POST',
    body: { to: toEmailOrUserId, amount, ...(note ? { note } : {}) },
  });
}
